"use client"

import { useEffect, useState } from "react"
import { useSearchParams } from "next/navigation"
import { EditorHeader } from "@/components/editor/editor-header"
import { EditorRibbonBar } from "@/components/editor/editor-ribbon-bar"
import { useThemeMode } from "@/lib/themes/theme-mode"
import { editorThemeVars } from "@/lib/themes/editor-theme-vars"
import {
  FileExplorerFrame,
  type SidebarTab,
} from "@/components/editor/FileExplorerFrame"
import { ProjectSettingsContent } from "@/components/editor/project-settings-panel"
import { DividerHandle } from "@/components/editor/divider-handle"
import { DockableLayout } from "@/components/editor/dock"
import { LayoutProvider } from "@/components/editor/dock/layout-context"
import { useResize } from "@/hooks/use-resize"
import { EditorStoreProvider } from "@/lib/editor-store"
import { DetachedWindowSync } from "@/components/editor/detached-window-sync"
import { SessionDisconnectedDialog } from "@/components/editor/session-disconnected-dialog"
import type { CreateSessionResponse } from "@/lib/compiler/types"

/* flexlayout-react themes (light base + our overrides) */
import "flexlayout-react/style/combined.css"
import "@/styles/flexlayout-overrides.css"

export default function EditorClientPage({
  initialSession,
}: {
  initialSession?: CreateSessionResponse | null
}) {
  const searchParams = useSearchParams()
  const { theme } = useThemeMode()
  const urlProjectName = searchParams.get("projectName")
  const urlProjectId = searchParams.get("projectId")
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("file")
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  /* ── Sidebar width (px) — the sidebar stays fixed, only the dock area is dynamic ── */
  const sidebar = useResize({
    axis: "horizontal",
    initial: 280,
    min: 200,
    max: 500,
    snapPoints: [280],
    storageKey: "layout-sidebar",
    collapseSize: 200,
  })

  const showSettings = sidebarTab === "setting"

  // After leaving Settings, force a resize so flexlayout/Monaco remeasure the visible dock.
  useEffect(() => {
    if (showSettings) return
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"))
    })
    return () => cancelAnimationFrame(id)
  }, [showSettings])

  // Collapsing/expanding the sidebar changes the dock's width — same remeasure.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"))
    })
    return () => cancelAnimationFrame(id)
  }, [sidebarCollapsed])

  return (
    <EditorStoreProvider initialSession={initialSession} projectName={urlProjectName} projectId={urlProjectId}>
      <DetachedWindowSync />
      <SessionDisconnectedDialog />
      <LayoutProvider>
        <div className="flex h-screen flex-col bg-background" style={editorThemeVars(theme)}>
          {/* Global Header */}
          <EditorHeader />

          {/* Ribbon Navigation — swaps to the PDF toolbar while a PDF tab has focus */}
          <EditorRibbonBar />

          {/* Main Workspace */}
          <div
              className={
                showSettings
                    ? "flex flex-1 overflow-hidden bg-[var(--ed-explorer-canvas-bg,#18181b)]"
                    : "flex flex-1 overflow-hidden bg-[var(--ed-explorer-canvas-bg,#18181b)]"
              }
          >
            {/* ─── Fixed Left Sidebar ─── */}
            <div
                ref={sidebar.targetRef}
                className="shrink-0 overflow-hidden bg-transparent"
                style={{ width: sidebarCollapsed ? "64px" : `${sidebar.value}px` }}
            >
              <FileExplorerFrame
                activeTab={sidebarTab}
                onActiveTabChange={setSidebarTab}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
              />
            </div>

            {/* Sidebar ↔ Dock divider — hidden while collapsed. Dragging would fight the
                resize hook, which writes width straight to the DOM from a stale
                pre-collapse base value, overriding the 64px collapsed width. */}
            {!sidebarCollapsed && (
              <DividerHandle
                axis="vertical"
                onMouseDown={sidebar.onMouseDown}
                onDoubleClick={sidebar.onDoubleClick}
                className={showSettings ? "bg-transparent" : undefined}
              />
            )}

            {/* ─── Settings + dock — keep both mounted so editor/PDF/console state survives tab switches ─── */}
            <div className="relative min-h-0 min-w-0 flex-1">
              <div
                className={
                  showSettings
                    ? "absolute inset-0 z-10 flex min-h-0 flex-col"
                    : "pointer-events-none absolute inset-0 z-0 hidden"
                }
                aria-hidden={!showSettings}
              >
                <ProjectSettingsContent />
              </div>
              <div
                className={
                  showSettings
                    ? "pointer-events-none absolute inset-0 z-0 invisible"
                    : "absolute inset-0 z-0 flex min-h-0 flex-col"
                }
                aria-hidden={showSettings}
              >
                <DockableLayout />
              </div>
            </div>
          </div>
        </div>
      </LayoutProvider>
    </EditorStoreProvider>
  )
}
